import type { ILoan, ILoanDebts } from "@/modules/loan/common/types";
import { useGetLoanRepaymentTemplateQuery } from "@/modules/template/services";
import {
  LoanRepaymentKeys,
  LoanRepaymentStatus,
  LoanRepaymentType,
  TransactionTypes,
  TransferKeys
} from "@/modules/transaction/common/enum";
import type {
  GetLoanByContractNumber,
  GetLoanPenalties,
  ILoanRepaymentForm,
  ILoanRepaymentRequest
} from "@/modules/transaction/common/types";
import { AmountInfo } from "@/modules/transaction/components/LoanRepayment/AmountInfo";
import { LoanSelectOption } from "@/modules/transaction/components/LoanRepayment/LoanSelectOption";
import { SaveAsTemplate } from "@/modules/transaction/components/SaveAsTemplate";
import { useLoanRepayment } from "@/modules/transaction/hooks";
import {
  useCreateLoanRepaymentMutation,
  useGetLoanRepaymentQuery,
  useUpdateLoanRepaymentMutation,
} from "@/modules/transaction/services";
import { CURRENCY, } from "@/shared/common/constants";
import { CloudflareAsset, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  Amount,
  CloudFlareImage,
  InfoContainer,
  OwnAccountSelectOption,
  Select,
  TransferLayout
} from "@/shared/components";
import { cleanupAmount, isValidAmount, mergeByKeys } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Text } from "@ab.uitools/ui-library/components/Text";
import classNames from "classnames";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  loan?: ILoan;
  isEdit?: boolean;
  isCopy?: boolean;
  penalties?: ILoanDebts;
  getLoan: GetLoanByContractNumber;
  getLoanPenalties: GetLoanPenalties;
  setAmountDistributionOpen: Dispatch<SetStateAction<boolean>>;
  isNameUnique: boolean;
  setIsNameUnique: Dispatch<SetStateAction<boolean>>;
}

export const LoanRepaymentForm = ({
  loan,
  getLoan,
  isEdit,
  isCopy,
  penalties,
  getLoanPenalties,
  setAmountDistributionOpen,
  isNameUnique,
  setIsNameUnique,
}: Props) => {
  const { t } = useTranslation();
  const idempotencyKey = useRef(uuid4()).current;
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get(SearchParamKeys.TemplateId);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: template,
    isLoading: isGetTemplateLoading,
  } = useGetLoanRepaymentTemplateQuery({
    id: templateId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !templateId
  });

  const {
    data: loanRepayment,
    isLoading: isGetTransferLoading,
  } = useGetLoanRepaymentQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const {
    reset,
    trigger,
    values,
    setValue,
    getLoanInfo,
    loanOptions,
    accountsList,
    loanContractNumber,
    accountsFilteredList,
    loanRepaymentTypeOptions,
  } = useLoanRepayment({
    isEdit,
    isCopy,
    getLoan,
    getLoanPenalties,
    currency: loan?.currency,
    savedAmount: loanRepayment?.amount ?? template?.amount,
  });

  const [createLoanRepaymentApi, { isLoading: isCreateTransferLoading }] = useCreateLoanRepaymentMutation();
  const [updateLoanRepaymentApi, { isLoading: isUpdateTransferLoading }] = useUpdateLoanRepaymentMutation();

  const loanRepaymentAction = useCallback((values: ILoanRepaymentForm) => {
    const requestData: ILoanRepaymentRequest = {
      ...values,
      amount: +cleanupAmount(`${values.amount}`),
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount
      ),
    };
    return isEdit ?
      updateLoanRepaymentApi({
        data: requestData,
        id: `${loanRepayment?.id}`
      }).unwrap() :
      createLoanRepaymentApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, loanRepayment]);

  const setDefaultValues = () => {
    const data = {
      loanContractNumber: loanRepayment?.loanContractNumber,
      loanRepaymentType: loanRepayment?.loanRepaymentType,
      debitAccount: loanRepayment?.debitAccount.number,
      transactionNumber: isEdit ?
        loanRepayment?.transactionNumber : values.transactionNumber,
      amount: loanRepayment?.amount,
      purpose: loanRepayment?.purpose,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    loanContractNumber: values.loanContractNumber,
    loanRepaymentType: values.loanRepaymentType,
    debitAccount: values.debitAccount,
    transactionNumber: values.transactionNumber,
    amount: +cleanupAmount(values.amount),
    purpose: values.purpose,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  const infoContent = useMemo(() => {
    if (!loan) return "";
    switch (values.loanRepaymentType) {
      case LoanRepaymentType.RegularRepayment:
        if (loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue) {
          return t("transfer.loanRepaymnet.paymenCoversOverdueSheduledPrincipal");
        } else if (values.amount > loan.upcomingPaymentAmount) {
          return t("transfer.loanRepayment.earlyPaymentAppliedPrincipalUpcoming");
        }
        return "";
      case LoanRepaymentType.PrincipalRepayment:
        if (loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue) {
          return t("transfer.loanRepaymnet.paymenCoversOverduePrincipal");
        } else if (loan.upcomingPaymentDate) {
          return t("transfer.loanRepayment.todayRepaymentDaySceduledFirsThenPrincipal");
        }
        return "";
      case LoanRepaymentType.FullRepayment:
        if (loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue) {
          return t("transfer.loanRepayment.paymentCoversPrincipalOverdueEarlyFee");
        } else {
          return t("transfer.loanRepayment.paymentCoversRemayningEarlyFee");
        }
      default:
        return "";
    }
  }, [loan, values.loanRepaymentType, values.amount]);

  useEffect(() => {
    if ((isEdit || isCopy) && !!loanRepayment?.id && loanOptions.length) {
      setDefaultValues();
    }
  }, [isEdit, isCopy, loanRepayment, loanOptions]);

  useEffect(() => {
    if (!isEdit && !isCopy) {
      setValue(LoanRepaymentKeys.LoanRepaymentType, LoanRepaymentType.RegularRepayment);
    }
  }, [isEdit, isCopy]);

  useEffect(() => {
    if (template) {
      const data = mergeByKeys(values, {
        ...template,
        debitAccount: template?.debitAccount?.number,
      });
      reset?.(data);
    }
  }, [template]);

  useEffect(() => {
    if (!isEdit && !isCopy && !templateId) {
      setValue(TransferKeys.Purpose, t("transfer.loanRepayment.purpose"));
    }
  }, []);

  return (
    <TransferLayout
      title={t("transfer.loanRepayment.title")}
      page="Loan repayment"
      accountsList={accountsList}
      isEdit={isEdit}
      hasChanges={hasChanges}
      withCommission={false}
      getTransferId={loanRepaymentAction}
      transactionType={TransactionTypes.LoanRepayment}
      isSaveDisabled={!isNameUnique && values.isSaveAsTemplate}
      isSignDisabled={!isNameUnique && values.isSaveAsTemplate}
      isLoading={isCreateTransferLoading || isUpdateTransferLoading}
      isGetTransferLoading={isGetTransferLoading || isGetTemplateLoading}
    >
      <>
        <CollapseGroup
          className="collapse-group"
          items={[
            {
              content: <>
                <FormField
                  name="loanContractNumber"
                  className="mb-32"
                  dataId="loanContractNumber"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      isDisabled={!!loanContractNumber}
                      options={loanOptions}
                      className="custom-select"
                      setSelectedValue={() => {
                        setValue(TransferKeys.DebitAccount, "");
                        setValue(TransferKeys.Amount, "");
                      }}
                      label={t("transfer.loanRepayment.selectLoan")}
                      placeholder={t("transfer.loanRepayment.chooseLoan")}
                      components={{ Option: LoanSelectOption }}
                    />
                  )}
                />
                {loan && values.loanContractNumber && (
                  <div className="flexbox align-items--center loan-info p-12 mb-32">
                    <div
                      className={classNames(
                        `loan-info--icon flexbox align-items--center justify-content--center`,
                        {
                          "loan-info--icon-danger": loan.loanRepaymentStatus === LoanRepaymentStatus.Overdue
                        }
                      )}>
                      <CloudFlareImage
                        size="small"
                        path={
                          loan.loanRepaymentStatus === LoanRepaymentStatus.Overdue ?
                            CloudflareAsset.LoanDangerLight :
                            CloudflareAsset.Loan
                        }
                      />
                    </div>
                    <div className="pl-12">
                      <Text weight="semibold">{t(loan.loanType)}</Text>
                      <div className="flexbox">
                        <Text type="tertiary">
                          {loan.contractNumber} • {loan.interestRate}%
                        </Text>
                        {loan.loanRepaymentStatus === LoanRepaymentStatus.Overdue && (
                          <>
                            <Text className="px-4" type="tertiary">•</Text>
                            <Text type="danger-light">
                              {t("transfer.loanRepayment.overdue")}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <FormField
                  name="loanRepaymentType"
                  className="mb-32"
                  dataId="loanRepaymentType"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      options={loanRepaymentTypeOptions}
                      label={t("transfer.loanRepayment.loanRepaymentType")}
                      placeholder={t("transfer.loanRepayment.chooseLoanRepaymentType")}
                    />
                  )}
                />
                <FormField
                  name="debitAccount"
                  className="mb-32"
                  dataId="debitAccount"
                  shouldValidate
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Select
                      {...props}
                      required
                      isSearchable
                      className="custom-select"
                      noOptionsMessage={() => {
                        return values.loanContructNumber ?
                          t("transfer.loanRepayment.haventAccessOrEmpty") :
                          t("transfer.loanRepayment.emptyDebitAccountList");
                      }}
                      options={accountsFilteredList || []}
                      label={t("transfer.debitAccount")}
                      placeholder={t("transfer.chooseDebitAccount")}
                      components={{ Option: OwnAccountSelectOption }}
                    />
                  )}
                />
                {values.loanRepaymentType === LoanRepaymentType.FullRepayment && loan ? (
                  <div>
                    <Text weight="semibold" className="mb-10">
                      {t("general.amount")}
                    </Text>
                    <Amount
                      position="left"
                      weight="semibold"
                      size="medium"
                      currency={loan.currency}
                      amount={loan.upcomingPaymentAmount || 0}
                    />
                  </div>
                ) : (
                  <FormField
                    name="amount"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        required
                        type="numeric"
                        allowNegative={false}
                        allowLeadingZeros={false}
                        label={t("general.amount")}
                        placeholder={t("general.enterAmount")}
                        thousandSeparator=","
                        onBlur={() => {
                          getLoanInfo();
                          trigger?.(TransferKeys.Amount);
                        }}
                        decimalScale={loan?.currency === CURRENCY.AMD.label ? 1 : 2}
                        rightIconProps={{
                          Component: loan?.currency ? CURRENCY[loan?.currency]?.symbolIcon : "",
                        }}
                        isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                      />
                    )}
                  />
                )}
                {!!values.loanContractNumber && (
                  <AmountInfo
                    loan={loan}
                    penalties={penalties}
                    contractNumber={values.loanContractNumber}
                    loanRepaymentType={values.loanRepaymentType}
                  />
                )}
                {!!penalties && values.loanContractNumber && values.loanRepaymentType && (
                  <div
                    onClick={() => setAmountDistributionOpen(true)}
                    className="loan-amount-distribution pb-12 mt-32">
                    <div className="flexbox align-items--center">
                      <div className="loan-amount-distribution--calendar">
                        <CloudFlareImage size="small" path={CloudflareAsset.Calendar}/>
                      </div>
                      <Text
                        className="pl-4">{t("transfer.loanRepayment.amountDistribution")}</Text>
                    </div>
                    <div className="loan-amount-distribution--arrow">
                      <CloudFlareImage size="xsmall" path={CloudflareAsset.ArrowShortRight}/>
                    </div>
                  </div>
                )}
                <SaveAsTemplate
                  setValue={setValue}
                  setIsNameUnique={setIsNameUnique}
                  isSaveAsTemplate={values.isSaveAsTemplate}
                />
              </>,
              title: t("transfer.transferDetails"),
              value: "transfer_details",
              isOpen: true,
            },
            {
              content: <>
                <FormField
                  name="transactionNumber"
                  className="mb-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      label={t("transfer.transactionNumber")}
                      placeholder={t("transfer.enterTransactionNumber")}
                    />
                  )}/>
              </>,
              title: t("general.otherDetails"),
              value: "other_details",
              isOpen: true,
            },
          ]}
        />
        {infoContent && (
          <div className="loan-repayment-info-container">
            <InfoContainer content={infoContent}/>
          </div>
        )}
      </>
    </TransferLayout>
  );
};
